/* header-nav.js — mobile navigation panel for sections/header.liquid
   (rebrand 2026, W2-2a 2a-2).

   Behaviour ports prototype-c js/main.js (mobile navigation block): the
   hamburger toggles a `nav-open` class + aria-expanded, and Escape closes
   the panel and returns focus to the toggle. The prototype has no
   click-outside dismissal, so none is added here.

   Editor-lifecycle conformance (CONTRACTS §14): guarded define, no work at
   parse time, all listeners bound in connectedCallback via an
   AbortController and released wholesale in disconnectedCallback, no
   module-level state — multiple instances stay independent, and an editor
   re-injection (shopify:section:load swaps the DOM, firing
   disconnected/connected) re-initialises cleanly with no double-binding.

   The section ships inert markup: the toggle carries [hidden] and the CSS
   keys its enhanced states on `header-nav:defined`, so when this file fails
   to load the nav renders as a static wrapped list and the toggle never
   becomes a focusable dead control. */

if (!customElements.get('header-nav')) {
  customElements.define(
    'header-nav',
    class HeaderNav extends HTMLElement {
      connectedCallback() {
        this.navToggle = this.querySelector('.header__nav-toggle');
        this.nav = this.querySelector('.header__nav');
        if (!this.navToggle || !this.nav) return; // menu unset — nothing to drive

        this.listeners = new AbortController();
        const { signal } = this.listeners;

        // Closed baseline on every (re)connect, so no state leaks across
        // editor re-injections or element moves.
        this.setExpanded(false);
        this.navToggle.removeAttribute('hidden');

        this.navToggle.addEventListener('click', () => this.setExpanded(!this.isOpen), { signal });
        document.addEventListener('keydown', this.onDocumentKeydown.bind(this), { signal });

        // Crossing to desktop resets the panel: the ≤860px CSS no longer
        // applies there, so an open state would otherwise linger silently.
        this.desktopQuery = window.matchMedia('(min-width: 861px)');
        this.desktopQuery.addEventListener(
          'change',
          (event) => {
            if (event.matches) this.setExpanded(false);
          },
          { signal }
        );
      }

      disconnectedCallback() {
        if (!this.listeners) return;
        this.listeners.abort();
        this.listeners = null;
        this.setExpanded(false);
        // Restore the inert parse-time shape for a possible re-connect.
        this.navToggle.setAttribute('hidden', '');
      }

      get isOpen() {
        return this.classList.contains('nav-open');
      }

      setExpanded(open) {
        this.classList.toggle('nav-open', open);
        this.navToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        // Sticky seam (documented, CONTRACTS §14.8): while the panel is
        // anchored open, keep the on-scroll-up header from hiding — the
        // same `preventHide` flag HeaderMenu and localization-form set.
        const wrapper = this.closest('.header-wrapper');
        if (wrapper) wrapper.preventHide = open;
      }

      onDocumentKeydown(event) {
        if (event.key !== 'Escape' || !this.isOpen) return;
        // Layered dismissal: an open search modal, submenu disclosure or
        // localization picker inside the header owns this Escape (their own
        // handlers close them on keyup); the panel closes on the next press.
        if (this.querySelector('details[open], .disclosure__list-wrapper:not([hidden])')) return;
        this.setExpanded(false);
        this.navToggle.focus(); // prototype main.js: Escape refocuses the toggle
      }
    }
  );
}
